/**
 * React Query wrappers for the Vault Documents HTTP surface
 * (Phase 12d-1b).
 *
 * One hook per route. Mutations invalidate the per-Holder branch
 * (`forHolder(holderId)`) so the list / detail / search caches all
 * refresh on any write. Detail queries are keyed by documentId so a
 * patch on /document/X doesn't invalidate the corpus.
 *
 * Consumed by the Vault Documents arche shell + the My Data dual-tab
 * integration.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  api,
  type VaultDocumentDto,
  type VaultDocumentReferenceTargetTypeDto,
  type VaultDocumentSensitivityDto,
  type VaultDocumentStatusDto,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export interface UseVaultDocumentsListFilters {
  status?: VaultDocumentStatusDto;
  tags?: string[];
  includeArchived?: boolean;
  includeDeleted?: boolean;
  /** Drives the Trash view. Mutually exclusive with default listing. */
  deletedOnly?: boolean;
  /** `null` = Holder root. `vdf_*` = specific folder. Omit to list
   * across every folder (legacy behavior + search). */
  folderId?: string | null;
}

export function useVaultDocumentsList(
  holderId: string | null,
  filters: UseVaultDocumentsListFilters = {},
) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.list(holderId ?? '', filters),
    queryFn: () => api.listVaultDocuments(holderId!, filters),
    select: (data) => data.documents,
    enabled: !!holderId,
  });
}

export function useVaultDocument(documentId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.detail(documentId ?? ''),
    queryFn: () => api.getVaultDocument(documentId!),
    select: (data) => data.document,
    enabled: !!documentId,
  });
}

export function useVaultDocumentVersions(documentId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.versions(documentId ?? ''),
    queryFn: () => api.listVaultDocumentVersions(documentId!),
    select: (data) => data.versions,
    enabled: !!documentId,
  });
}

/** Fetches a fresh 1-hour signed download URL on demand. Not auto-
 * cached because the URL is short-lived; callers typically trigger
 * this from a button click, then `window.open(url)`. */
export function useVaultDocumentDownload() {
  return useMutation({
    mutationFn: (documentId: string) => api.getVaultDocumentDownloadUrl(documentId),
  });
}

/** Restore an older version to current. `documentId` identifies the
 * chain; `versionId` is the older version to bring forward. Busts the
 * Holder branch (the chain head moved → list + detail + versions all
 * refresh) and the detail of the new head. */
export function useRestoreVaultDocumentVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; versionId: string }) =>
      api.restoreVaultDocumentVersion(input.documentId, input.versionId),
    onSuccess: (data, variables) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
      // The chain the user was viewing also needs its versions list +
      // detail refreshed (the old head is now superseded).
      invalidateDocDetail(qc, variables.documentId);
      qc.invalidateQueries({
        queryKey: queryKeys.vaultDocuments.versions(variables.documentId),
      });
    },
  });
}

export interface UseVaultDocumentsSearchParams {
  query: string;
  topK?: number;
  minSimilarity?: number;
  includeContent?: boolean;
}

export function useVaultDocumentsSearch(
  holderId: string | null,
  params: UseVaultDocumentsSearchParams,
  options?: { enabled?: boolean },
) {
  // The query embedding burns a credit per search (AI Markup
  // Invariant), so the consuming component should gate the
  // `enabled` flag — typically tying it to a debounced "submit"
  // rather than every keystroke.
  return useQuery({
    queryKey: queryKeys.vaultDocuments.search(holderId ?? '', params),
    queryFn: () =>
      api.searchVaultDocuments({
        holderId: holderId!,
        query: params.query,
        topK: params.topK,
        minSimilarity: params.minSimilarity,
        includeContent: params.includeContent,
      }),
    select: (data) => data.matches,
    enabled: !!holderId && !!params.query.trim() && options?.enabled !== false,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

function invalidateHolderBranch(qc: QueryClient, holderId: string) {
  qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.forHolder(holderId) });
}

function invalidateDocDetail(qc: QueryClient, documentId: string) {
  qc.invalidateQueries({ queryKey: queryKeys.vaultDocuments.detail(documentId) });
}

/**
 * Upload-init mutation. Returns the presigned URL + headers; the
 * component is responsible for the direct S3 PUT and the followup
 * finalize call. We don't combine the three into a single mutation
 * because the PUT is browser-only (the api client can't see the
 * progress events) and finalize wants its own error surface.
 */
export function useVaultDocumentUploadInit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.initVaultDocumentUpload>[0]) =>
      api.initVaultDocumentUpload(input),
    onSuccess: (_data, variables) => {
      // The new pending row already counts toward quota — invalidate
      // the Holder branch so the storage usage widget refreshes.
      invalidateHolderBranch(qc, variables.holderId);
    },
  });
}

export function useVaultDocumentFinalize() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; checksumSha256: string }) =>
      api.finalizeVaultDocumentUpload(input.documentId, {
        checksumSha256: input.checksumSha256,
      }),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

export function useUpdateVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      documentId: string;
      updates: Parameters<typeof api.updateVaultDocument>[1];
    }) => api.updateVaultDocument(input.documentId, input.updates),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

export function useArchiveVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api.archiveVaultDocument(documentId),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

export function useUnarchiveVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api.unarchiveVaultDocument(documentId),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

export function useSetVaultDocumentSensitivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; sensitivity: VaultDocumentSensitivityDto }) =>
      api.setVaultDocumentSensitivity(input.documentId, input.sensitivity),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

export function useSoftDeleteVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api.softDeleteVaultDocument(documentId),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

export function useRestoreVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (documentId: string) => api.restoreVaultDocument(documentId),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

/** Move a document into a different folder (or root). The server
 * returns the updated row; we bust the Holder branch so source-folder
 * + target-folder + root list caches all refresh. */
export function useMoveVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; targetFolderId: string | null }) =>
      api.moveVaultDocument(input.documentId, input.targetFolderId),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.document.holderId);
      invalidateDocDetail(qc, data.document.documentId);
    },
  });
}

/** Hard-delete a single soft-deleted document. Caller passes
 * `holderId` so we can bust the per-Holder branch — the response has
 * no document body (the row is gone). */
export function usePurgeVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; holderId: string }) =>
      api.purgeVaultDocument(input.documentId),
    onSuccess: (_data, variables) => {
      invalidateHolderBranch(qc, variables.holderId);
      invalidateDocDetail(qc, variables.documentId);
    },
  });
}

/** Empty trash — server reaps up to the per-call batch cap and
 * returns `{ purgedCount, remainingCount, failures }`. The caller
 * loops while `remainingCount > 0`. */
export function useEmptyVaultTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (holderId: string) => api.emptyVaultTrash(holderId),
    onSuccess: (_data, holderId) => {
      invalidateHolderBranch(qc, holderId);
    },
  });
}

// ---------------------------------------------------------------------------
// High-level "do an upload" composite
// ---------------------------------------------------------------------------

/**
 * Compute SHA-256 of a File client-side. Used by uploaders before
 * calling upload-init (the server signs the presigned PUT with the
 * checksum so S3 rejects mismatched bytes). Returns lowercase hex.
 */
export async function computeFileSha256Hex(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface PerformUploadInput {
  holderId: string;
  file: File;
  sensitivity?: VaultDocumentSensitivityDto;
  description?: string;
  tags?: string[];
  /** Optional rename for the stored doc; defaults to file.name. */
  displayName?: string;
  /** Target folder for the new document. NULL/omitted = Holder root. */
  folderId?: string | null;
}

/**
 * End-to-end uploader: SHA-256 the file, call upload-init, PUT bytes
 * direct to S3 with the signed headers, then finalize. Returns the
 * finalized document row.
 *
 * Throws on any step failure — caller surfaces the error.
 */
export function useUploadVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PerformUploadInput): Promise<VaultDocumentDto> => {
      const checksumSha256 = await computeFileSha256Hex(input.file);
      const init = await api.initVaultDocumentUpload({
        holderId: input.holderId,
        name: input.displayName ?? input.file.name,
        filename: input.file.name,
        mimeType: input.file.type || 'application/octet-stream',
        sizeBytes: input.file.size,
        checksumSha256,
        sensitivity: input.sensitivity,
        description: input.description,
        tags: input.tags,
        folderId: input.folderId ?? null,
      });

      // Direct-to-S3 PUT. The signed URL pins ContentType +
      // ContentLength + checksum + the pending tag — any mismatch
      // fails the S3 signature.
      const putResponse = await fetch(init.uploadUrl, {
        method: 'PUT',
        headers: init.requiredHeaders,
        body: input.file,
      });
      if (!putResponse.ok) {
        throw new Error(`S3 PUT failed (${putResponse.status}): the upload didn't complete.`);
      }

      const finalized = await api.finalizeVaultDocumentUpload(init.document.documentId, {
        checksumSha256,
      });
      return finalized.document;
    },
    onSuccess: (document) => {
      invalidateHolderBranch(qc, document.holderId);
    },
  });
}

// ---------------------------------------------------------------------------
// Cross-arche attachment (Phase 12e-1)
// ---------------------------------------------------------------------------

export function useVaultDocumentAttachments(documentId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.attachmentsForDocument(documentId ?? ''),
    queryFn: () => api.listVaultDocumentAttachmentsForDocument(documentId!),
    select: (data) => data.references,
    enabled: !!documentId,
  });
}

export function useVaultDocumentsForTarget(
  regardingType: VaultDocumentReferenceTargetTypeDto | null,
  regardingId: string | null,
) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.attachmentsForTarget(regardingType ?? '', regardingId ?? ''),
    queryFn: () => api.listVaultDocumentsForTarget(regardingType!, regardingId!),
    enabled: !!regardingType && !!regardingId,
  });
}

export function useAttachVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      documentId: string;
      regardingType: VaultDocumentReferenceTargetTypeDto;
      regardingId: string;
    }) =>
      api.attachVaultDocument(input.documentId, {
        regardingType: input.regardingType,
        regardingId: input.regardingId,
      }),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({
        queryKey: queryKeys.vaultDocuments.attachmentsForDocument(variables.documentId),
      });
      qc.invalidateQueries({
        queryKey: queryKeys.vaultDocuments.attachmentsForTarget(
          variables.regardingType,
          variables.regardingId,
        ),
      });
      // Also bust the holder branch so list views re-fetch (the
      // document itself didn't change, but the consumer's UI may
      // also surface attachment counts).
      invalidateHolderBranch(qc, data.reference.holderId);
    },
  });
}

export function useDetachVaultDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { documentId: string; referenceId: string }) =>
      api.detachVaultDocumentReference(input.documentId, input.referenceId),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({
        queryKey: queryKeys.vaultDocuments.attachmentsForDocument(variables.documentId),
      });
      qc.invalidateQueries({
        queryKey: queryKeys.vaultDocuments.attachmentsForTarget(
          data.reference.regardingType,
          data.reference.regardingId,
        ),
      });
      invalidateHolderBranch(qc, data.reference.holderId);
    },
  });
}

// ---------------------------------------------------------------------------
// Storage usage (review fix #9)
// ---------------------------------------------------------------------------

export function useVaultDocumentsStorage(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.storage(holderId ?? ''),
    queryFn: () => api.getVaultDocumentsStorage(holderId!),
    // Returns { storage, principal }. The free-tier cap is enforced
    // at the billing-principal level — UI usage bars should bind to
    // `principal` for the actual quota; `storage` is the per-Holder
    // breakdown.
    enabled: !!holderId,
  });
}

// ---------------------------------------------------------------------------
// Folders (vault_document_folders)
// ---------------------------------------------------------------------------

/** Flat list of every folder in the Holder. Consumer assembles the
 * tree from `parentFolderId` pointers. Single cache slot — folder
 * lists are small and the My Data UI needs the whole tree on render. */
export function useVaultDocumentFolders(holderId: string | null) {
  return useQuery({
    queryKey: queryKeys.vaultDocuments.folders(holderId ?? ''),
    queryFn: () => api.listVaultDocumentFolders(holderId!),
    select: (data) => data.folders,
    enabled: !!holderId,
  });
}

export function useCreateVaultDocumentFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { holderId: string; parentFolderId?: string | null; name: string }) =>
      api.createVaultDocumentFolder(input),
    onSuccess: (data) => {
      invalidateHolderBranch(qc, data.folder.holderId);
    },
  });
}

export function useRenameVaultDocumentFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { folderId: string; name: string }) =>
      api.renameVaultDocumentFolder(input.folderId, input.name),
    onSuccess: (data) => {
      // Rename rewrites `path` on every descendant folder + every
      // descendant document, so the whole Holder branch may shift —
      // bust it all.
      invalidateHolderBranch(qc, data.folder.holderId);
    },
  });
}

/** Delete a user-managed folder. Caller passes `holderId` so we can
 * invalidate the per-Holder branch — the 204 response carries no
 * body to derive it from. */
export function useDeleteVaultDocumentFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { folderId: string; holderId: string; force?: boolean }) =>
      api.deleteVaultDocumentFolder(input.folderId, { force: input.force }),
    onSuccess: (_data, variables) => {
      invalidateHolderBranch(qc, variables.holderId);
    },
  });
}

/** Self-heal mutation — re-runs server-side `ensureSystemFolders` for a
 * Holder. The route is idempotent (ON CONFLICT DO NOTHING on the
 * `(holder_id, path)` unique index), so calling it on a Holder that
 * already has the scaffold is a no-op insert + a single SELECT.
 *
 * Use case: Holders created before the folder-substrate commit
 * (`e80e6677`, 2026-05-18) never had `ensureSystemFolders` called at
 * provisioning time, so they have zero folders. The DocumentsListPage
 * fires this once per Holder when the folders query lands empty —
 * patching legacy Holders the moment a user visits them. New Holders
 * already get the scaffold inside their creation transaction; this
 * hook is the migration path, not the steady-state path. */
export function useEnsureVaultSystemFolders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (holderId: string) => api.ensureVaultSystemFolders(holderId),
    onSuccess: (_data, holderId) => {
      // Bust the whole Holder branch so the folders query refetches and
      // the documents-list query picks up the new folder_id values it
      // can now filter on.
      invalidateHolderBranch(qc, holderId);
    },
  });
}
